import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BellRing, Building2, Globe2, KeyRound, LogOut, Mail, Plus, RefreshCw, Send, ShieldCheck, Sparkles, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  adminApi,
  CreateAdminPayload,
  getApiErrorMessage,
  ReminderScope,
  SendSuperAdminReminderPayload,
  SendSuperAdminReminderResponse,
  SuperAdminDirectoryUser,
} from '@/lib/api';
import { UserProfile } from '@/types';

interface SuperAdminDashboardProps {
  currentUser: UserProfile;
  onLogout: () => void;
}

type UserTabKey = 'all' | 'drivers' | 'riders' | 'admins' | 'superAdmins';

const USER_TAB_META: Record<UserTabKey, { label: string; scope: ReminderScope | null }> = {
  all: { label: 'All Users', scope: 'all_users' },
  drivers: { label: 'Drivers', scope: 'drivers' },
  riders: { label: 'Riders', scope: 'riders' },
  admins: { label: 'Admins', scope: 'admins' },
  superAdmins: { label: 'Super Admins', scope: null },
};

const SuperAdminDashboard = ({ currentUser, onLogout }: SuperAdminDashboardProps) => {
  const queryClient = useQueryClient();
  const [activeUserTab, setActiveUserTab] = useState<UserTabKey>('all');
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [newAdmin, setNewAdmin] = useState<CreateAdminPayload>({
    name: '',
    email: '',
    password: '',
    phone_number: '',
    school_name: '',
  });
  const [reminderForm, setReminderForm] = useState<SendSuperAdminReminderPayload>({
    scope: 'all_users',
    subject: '',
    headline: '',
    message: '',
    schoolDomain: '',
    userIds: [],
    ctaLabel: '',
    ctaUrl: '',
  });
  const [lastReminderResult, setLastReminderResult] = useState<SendSuperAdminReminderResponse | null>(null);

  const { data: statsData, isLoading, isRefetching, refetch } = useQuery({
    queryKey: ['superAdminStats'],
    queryFn: adminApi.getSuperStats,
    enabled: currentUser.role === 'super_admin',
  });

  const refreshAll = () => {
    queryClient.invalidateQueries({ queryKey: ['superAdminStats'] });
    refetch();
  };

  const createAdminMutation = useMutation({
    mutationFn: adminApi.createAdminAccount,
    onSuccess: () => {
      toast.success('Admin account created successfully.');
      setNewAdmin({ name: '', email: '', password: '', phone_number: '', school_name: '' });
      refreshAll();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to create admin account.'));
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: adminApi.deleteUser,
    onSuccess: (_, userId) => {
      toast.success('User removed successfully.');
      setSelectedUserIds((prev) => prev.filter((selectedId) => selectedId !== userId));
      refreshAll();
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to remove user.'));
    },
  });

  const sendReminderMutation = useMutation({
    mutationFn: adminApi.sendReminder,
    onSuccess: (result) => {
      setLastReminderResult(result);
      toast.success(result.message || 'Reminder request completed.');
      if (result.scope === 'selected_users') {
        setSelectedUserIds([]);
      }
    },
    onError: (error) => {
      toast.error(getApiErrorMessage(error, 'Failed to send reminder.'));
    },
  });

  const visibleUsers = useMemo<SuperAdminDirectoryUser[]>(
    () => statsData?.users[activeUserTab] ?? [],
    [activeUserTab, statsData],
  );

  const summaryCards = useMemo(() => {
    const stats = statsData?.stats;
    if (!stats) return [];
    return [
      { label: 'Total Users', value: stats.totalUsers, icon: Users },
      { label: 'Students', value: stats.totalStudents, icon: Building2 },
      { label: 'Drivers', value: stats.totalDrivers, icon: Globe2 },
      { label: 'Admins', value: stats.totalAdmins, icon: ShieldCheck },
      { label: 'Rides', value: stats.totalRides, icon: RefreshCw },
      { label: 'Posts', value: stats.totalPosts, icon: Mail },
    ];
  }, [statsData]);

  const availableScopes = statsData?.reminders.availableScopes ?? [];
  const selectedScope = reminderForm.scope;

  const handleCreateAdmin = () => {
    if (!newAdmin.name.trim() || !newAdmin.email.trim() || !newAdmin.password.trim()) {
      toast.error('Name, email, and password are required.');
      return;
    }

    if (newAdmin.password.trim().length < 8) {
      toast.error('Password must be at least 8 characters.');
      return;
    }

    createAdminMutation.mutate({
      name: newAdmin.name.trim(),
      email: newAdmin.email.trim(),
      password: newAdmin.password.trim(),
      phone_number: newAdmin.phone_number?.trim() || undefined,
      school_name: newAdmin.school_name?.trim() || undefined,
    });
  };

  const confirmDelete = (userId: string, email: string) => {
    if (userId === currentUser.id) {
      toast.error('You cannot delete the currently logged-in super admin account.');
      return;
    }

    if (window.confirm(`Delete user ${email}?`)) {
      deleteUserMutation.mutate(userId);
    }
  };

  const toggleSelectedUser = (userId: string) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleReminderSubmit = () => {
    if (!reminderForm.subject?.trim() || !reminderForm.headline?.trim() || !reminderForm.message?.trim()) {
      toast.error('Subject, headline, and message are required.');
      return;
    }

    if (selectedScope === 'school' && !reminderForm.schoolDomain?.trim()) {
      toast.error('School domain is required for school reminders.');
      return;
    }

    if (selectedScope === 'selected_users' && selectedUserIds.length === 0) {
      toast.error('Select at least one user before sending a targeted reminder.');
      return;
    }

    sendReminderMutation.mutate({
      ...reminderForm,
      scope: selectedScope,
      schoolDomain: reminderForm.schoolDomain?.trim() || undefined,
      userIds: selectedScope === 'selected_users' ? selectedUserIds : undefined,
      ctaLabel: reminderForm.ctaLabel?.trim() || undefined,
      ctaUrl: reminderForm.ctaUrl?.trim() || undefined,
    });
  };

  const roleBadgeClass = (role: string) => {
    if (role === 'super_admin') return 'bg-amber-500/10 text-amber-600';
    if (role === 'admin') return 'bg-primary/10 text-primary';
    if (role === 'driver') return 'bg-emerald-500/10 text-emerald-600';
    return 'bg-accent/10 text-accent';
  };

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
          <p className="animate-pulse text-muted-foreground">Loading super admin dashboard...</p>
        </div>
      </div>
    );
  }

  if (!statsData) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-6">
        <div className="max-w-lg rounded-3xl border border-border bg-card p-8 text-center shadow-warm">
          <h1 className="font-display text-2xl font-bold text-foreground">Dashboard unavailable</h1>
          <p className="mt-3 text-sm leading-6 text-muted-foreground">
            The platform dashboard could not be loaded. Refresh or sign in again to restore the session.
          </p>
          <div className="mt-6 flex justify-center gap-3">
            <Button variant="outline" onClick={() => refetch()}>
              Try again
            </Button>
            <Button onClick={onLogout}>Sign out</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="absolute inset-x-0 top-0 h-80 bg-[radial-gradient(circle_at_top_left,_hsla(25,85%,55%,0.2),_transparent_42%),radial-gradient(circle_at_top_right,_hsla(190,60%,45%,0.16),_transparent_36%)]" />
      <main className="relative mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">
        <header className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm-lg backdrop-blur">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 rounded-full bg-secondary/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-secondary-foreground">
                Super Admin
              </div>
              <h1 className="mt-4 font-display text-4xl font-bold text-foreground">Platform administration</h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-muted-foreground">
                Monitoring all schools, all users, and broadcast reminders from the new global super admin contract.
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={refreshAll} disabled={isRefetching}>
                <RefreshCw className={`mr-2 h-4 w-4 ${isRefetching ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" onClick={onLogout}>
                <LogOut className="mr-2 h-4 w-4" />
                Sign out
              </Button>
            </div>
          </div>

          <div className="mt-6 grid gap-3 md:grid-cols-3">
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Signed In As</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{currentUser.email}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">New Users (7 days)</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{statsData.stats.usersLast7Days}</p>
            </div>
            <div className="rounded-2xl border border-border/70 bg-background/70 p-4">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground">Registered Colleges</p>
              <p className="mt-2 text-lg font-semibold text-foreground">{statsData.stats.totalRegisteredColleges}</p>
            </div>
          </div>
        </header>

        <section className="mt-8 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {summaryCards.map(({ label, value, icon: Icon }) => (
            <div key={label} className="rounded-3xl border border-border/70 bg-card/90 p-5 shadow-warm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{label}</p>
                  <p className="mt-3 font-display text-3xl font-bold text-foreground">{value}</p>
                </div>
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <Icon className="h-5 w-5" />
                </div>
              </div>
            </div>
          ))}
        </section>

        <section className="mt-8 grid gap-8 xl:grid-cols-[0.9fr_1.1fr]">
          <div className="space-y-8">
            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">Create School Admin</h2>
                <p className="mt-1 text-sm text-muted-foreground">Provision a new school admin account from the platform control panel.</p>
              </div>

              <div className="mt-5 rounded-[1.75rem] border border-primary/15 bg-[linear-gradient(135deg,hsla(25,85%,55%,0.08),hsla(190,60%,45%,0.08))] p-5">
                <div className="flex items-start gap-3">
                  <div className="rounded-2xl bg-card/90 p-3 text-primary shadow-warm">
                    <Sparkles className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="font-display text-lg font-bold text-foreground">Backend-connected provisioning</p>
                    <p className="mt-1 text-sm leading-6 text-muted-foreground">
                      Password is now required and sent directly to the updated admin creation endpoint. Phone number and school name remain optional.
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-5 grid gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-foreground">Admin name *</label>
                  <Input
                    placeholder="School Admin Name"
                    value={newAdmin.name}
                    onChange={(e) => setNewAdmin((prev) => ({ ...prev, name: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-foreground">Email *</label>
                  <Input
                    type="email"
                    placeholder="admin@school.edu"
                    value={newAdmin.email}
                    onChange={(e) => setNewAdmin((prev) => ({ ...prev, email: e.target.value }))}
                  />
                </div>
                <div className="grid gap-2">
                  <div className="flex items-center justify-between gap-3">
                    <label className="text-sm font-medium text-foreground">Password *</label>
                    <span className={`text-xs font-semibold ${newAdmin.password.trim().length >= 8 ? 'text-emerald-600' : 'text-muted-foreground'}`}>
                      {newAdmin.password.trim().length}/8 minimum
                    </span>
                  </div>
                  <div className="relative">
                    <KeyRound className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      type="password"
                      placeholder="Minimum 8 characters"
                      value={newAdmin.password}
                      onChange={(e) => setNewAdmin((prev) => ({ ...prev, password: e.target.value }))}
                      className="pl-10"
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">
                    The backend stores the password hash. Use a strong temporary password when provisioning.
                  </p>
                </div>
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="grid gap-2">
                    <label className="text-sm font-medium text-foreground">Phone number</label>
                    <Input
                      placeholder="Optional"
                      value={newAdmin.phone_number}
                      onChange={(e) => setNewAdmin((prev) => ({ ...prev, phone_number: e.target.value }))}
                    />
                  </div>
                  <div className="grid gap-2">
                    <label className="text-sm font-medium text-foreground">School name</label>
                    <Input
                      placeholder="Optional"
                      value={newAdmin.school_name}
                      onChange={(e) => setNewAdmin((prev) => ({ ...prev, school_name: e.target.value }))}
                    />
                  </div>
                </div>
                <Button onClick={handleCreateAdmin} disabled={createAdminMutation.isPending || newAdmin.password.trim().length < 8}>
                  <Plus className="mr-2 h-4 w-4" />
                  Create Admin Account
                </Button>
              </div>
            </div>

            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground">Schools</h2>
                  <p className="mt-1 text-sm text-muted-foreground">School totals are derived from user email domains by the backend.</p>
                </div>
                <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                  {statsData.schools.length} schools
                </span>
              </div>

              <div className="mt-5 space-y-3">
                {statsData.schools.map((school) => (
                  <div key={school.schoolDomain} className="rounded-2xl border border-border/70 bg-background/70 p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <p className="font-semibold text-foreground">{school.schoolName || 'Unknown School'}</p>
                        <p className="text-sm text-muted-foreground">{school.schoolDomain || 'No domain'}</p>
                      </div>
                      <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                        {school.totalUsers} users
                      </span>
                    </div>
                    <div className="mt-3 grid gap-2 text-sm text-muted-foreground sm:grid-cols-3">
                      <p>Total students: {school.totalStudents}</p>
                      <p>Total admins: {school.totalAdmins}</p>
                      <p>Total rides: {school.totalRides}</p>
                      <p>Total posts: {school.totalPosts}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
              <div className="flex items-center gap-3">
                <div className="rounded-2xl bg-primary/10 p-3 text-primary">
                  <BellRing className="h-5 w-5" />
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold text-foreground">Reminder Composer</h2>
                  <p className="mt-1 text-sm text-muted-foreground">
                    Uses {statsData.reminders.sendEndpoint || '/api/admin/super/reminders/send'}.
                  </p>
                </div>
              </div>

              <div className="mt-5 grid gap-4">
                <div className="grid gap-2">
                  <label className="text-sm font-medium text-foreground">Scope</label>
                  <select
                    value={selectedScope}
                    onChange={(e) => setReminderForm((prev) => ({ ...prev, scope: e.target.value as ReminderScope }))}
                    className="h-12 rounded-xl border border-border bg-background px-4 text-sm"
                  >
                    {availableScopes.map((scope) => (
                      <option key={scope} value={scope}>
                        {scope}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedScope === 'school' && (
                  <div className="grid gap-2">
                    <label className="text-sm font-medium text-foreground">School Domain</label>
                    <Input
                      placeholder="und.edu"
                      value={reminderForm.schoolDomain ?? ''}
                      onChange={(e) => setReminderForm((prev) => ({ ...prev, schoolDomain: e.target.value }))}
                    />
                  </div>
                )}

                {selectedScope === 'selected_users' && (
                  <div className="rounded-2xl border border-border/70 bg-background/70 p-4 text-sm text-muted-foreground">
                    {selectedUserIds.length} users selected from the user lists.
                  </div>
                )}

                <Input
                  placeholder="Email subject"
                  value={reminderForm.subject}
                  onChange={(e) => setReminderForm((prev) => ({ ...prev, subject: e.target.value }))}
                />
                <Input
                  placeholder="Headline"
                  value={reminderForm.headline}
                  onChange={(e) => setReminderForm((prev) => ({ ...prev, headline: e.target.value }))}
                />
                <Textarea
                  placeholder="Write the reminder body"
                  value={reminderForm.message}
                  onChange={(e) => setReminderForm((prev) => ({ ...prev, message: e.target.value }))}
                  className="min-h-32 rounded-2xl"
                />
                <div className="grid gap-4 md:grid-cols-2">
                  <Input
                    placeholder="CTA label (optional)"
                    value={reminderForm.ctaLabel ?? ''}
                    onChange={(e) => setReminderForm((prev) => ({ ...prev, ctaLabel: e.target.value }))}
                  />
                  <Input
                    placeholder="CTA URL (optional)"
                    value={reminderForm.ctaUrl ?? ''}
                    onChange={(e) => setReminderForm((prev) => ({ ...prev, ctaUrl: e.target.value }))}
                  />
                </div>
                <Button onClick={handleReminderSubmit} disabled={sendReminderMutation.isPending}>
                  <Send className="mr-2 h-4 w-4" />
                  Send Reminder
                </Button>
              </div>

              {lastReminderResult && (
                <div className="mt-5 rounded-2xl border border-border/70 bg-background/70 p-4 text-sm">
                  <p className="font-medium text-foreground">{lastReminderResult.message}</p>
                  <div className="mt-2 grid gap-2 text-muted-foreground sm:grid-cols-2">
                    <p>Scope: {lastReminderResult.scope}</p>
                    <p>Targeted: {lastReminderResult.targeted}</p>
                    <p>Sent: {lastReminderResult.sent}</p>
                    <p>Failed: {lastReminderResult.failed}</p>
                  </div>
                  {lastReminderResult.failedEmails.length > 0 && (
                    <p className="mt-3 text-xs text-muted-foreground">
                      Failed emails: {lastReminderResult.failedEmails.join(', ')}
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-[2rem] border border-border/70 bg-card/95 p-6 shadow-warm">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="font-display text-2xl font-bold text-foreground">Platform Users</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Browse every visible user group from the expanded `/admin/super/stats` payload.
                </p>
              </div>
              <span className="rounded-full bg-secondary px-3 py-1 text-xs font-semibold text-secondary-foreground">
                {visibleUsers.length} users in view
              </span>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              {(Object.keys(USER_TAB_META) as UserTabKey[]).map((tabKey) => (
                <button
                  key={tabKey}
                  onClick={() => setActiveUserTab(tabKey)}
                  className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                    activeUserTab === tabKey
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {USER_TAB_META[tabKey].label} ({statsData.users[tabKey].length})
                </button>
              ))}
            </div>

            <div className="mt-5 space-y-3">
              {visibleUsers.map((user) => (
                <div key={user.id} className="rounded-2xl border border-border/70 bg-background/70 p-4">
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="flex gap-3">
                      <input
                        type="checkbox"
                        checked={selectedUserIds.includes(user.id)}
                        onChange={() => toggleSelectedUser(user.id)}
                        className="mt-1 h-4 w-4 rounded border-border"
                      />
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-foreground">{user.name}</p>
                          <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${roleBadgeClass(user.role)}`}>
                            {user.role}
                          </span>
                        </div>
                        <p className="mt-1 text-sm text-muted-foreground">{user.email}</p>
                        <div className="mt-2 grid gap-1 text-xs text-muted-foreground sm:grid-cols-2">
                          <p>School: {user.schoolName || 'Unknown'}</p>
                          <p>Domain: {user.schoolDomain || 'Unknown'}</p>
                          <p>Phone: {user.phone_number || 'N/A'}</p>
                          <p>Created: {user.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Unknown'}</p>
                        </div>
                      </div>
                    </div>

                    <Button
                      variant="destructive"
                      size="sm"
                      disabled={deleteUserMutation.isPending || user.id === currentUser.id}
                      onClick={() => confirmDelete(user.id, user.email)}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      Delete
                    </Button>
                  </div>
                </div>
              ))}

              {visibleUsers.length === 0 && (
                <p className="rounded-2xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                  No users are available in this scope.
                </p>
              )}
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default SuperAdminDashboard;
